import React from "react";

import { DocumentText } from "../../../components/icons/svg/outline";

/**
 * Whether a payout shift has a worker work description.
 * Blank or whitespace-only strings are treated as not present.
 *
 * @param {string|null|undefined} workDescription Shift `workDescription` from the payout payload.
 * @returns {boolean} True when the worker provided a non-blank description.
 */
const hasWorkDescription = (workDescription) =>
  Boolean(workDescription && String(workDescription).trim());

/**
 * Notes icon for a payout-timesheet shift's worker work description.
 * Status-only states use a native tooltip. A present description uses the same
 * gray hover card style as the time-conflict tooltips on this page.
 *
 * @param {Object} props Component props.
 * @param {string|null|undefined} props.workDescription Shift work description (`DailyTimeSheet.description`).
 * @param {number|null|undefined} props.shiftId Shift id; null/undefined means a newly added payout shift.
 * @param {string} [props.translatedWorkDescription] Default-language hover text. Display only; never persisted.
 * @returns {JSX.Element}
 */
const WorkDescriptionInformation = ({
  workDescription,
  shiftId,
  translatedWorkDescription,
}) => {
  // Same added-shift check as ScopeInformation (job-punch icon)
  const isAddedShift = shiftId === null || shiftId === undefined;

  if (isAddedShift) {
    return (
      <div className="flex items-center justify-center" title="Payout added shift">
        <DocumentText className="h-4 w-4 text-gray-500" />
      </div>
    );
  }

  const hasDescription = hasWorkDescription(workDescription);

  if (!hasDescription) {
    return (
      <div
        className="flex items-center justify-center"
        title="Work Description Not Provided By Worker"
      >
        <DocumentText className="h-4 w-4 text-red-600" />
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-center relative">
      <DocumentText className="h-4 w-4 textblack" />
      <div className="absolute z-10 w-64 shadow-lg px-2 py-1 rounded text-xs top-full mt-1 left-1/2 transform -translate-x-1/2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 delay-100 pointer-events-none bg-gray-100">
        <div className="font-semibold">
          {translatedWorkDescription
            ? "Work Description (Auto-Translated):"
            : "Work Description:"}
        </div>
        {translatedWorkDescription ? (
          <>
            <div className="whitespace-pre-wrap break-words">
              {workDescription}
            </div>
            <div className="my-1 border-t border-gray-200" />
            <div className="whitespace-pre-wrap break-words text-gray-500">
              {translatedWorkDescription}
            </div>
          </>
        ) : (
          <div className="whitespace-pre-wrap break-words">
            {workDescription}
          </div>
        )}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-100"></div>
      </div>
    </div>
  );
};

export default WorkDescriptionInformation;
